import { Capacitor, registerPlugin } from '@capacitor/core';

/**
 * Bridge to the Android app's CapsicoIntegrity plugin (android/…/CapsicoIntegrityPlugin.java), which
 * asks Google Play Integrity for a token bound to the server's one-time nonce. iOS (App Attest) is
 * built on the server but dormant, so iOS reports "unsupported"; a browser has no native bridge.
 */

interface IntegrityPlugin {
  requestToken(options: { nonce: string; cloudProjectNumber: string }): Promise<{ token: string }>;
}

const CapsicoIntegrity = registerPlugin<IntegrityPlugin>('CapsicoIntegrity');

export type NativeIntegrityPlatform = 'android' | 'ios';

/** The native platform this build runs on, or null in a browser. */
export function nativeIntegrityPlatform(): NativeIntegrityPlatform | null {
  if (!Capacitor.isNativePlatform()) return null;
  const platform = Capacitor.getPlatform();
  return platform === 'android' || platform === 'ios' ? platform : null;
}

/** Play Integrity token for the nonce. Rejects when Play services are missing or the call fails. */
export async function playIntegrityToken(
  nonce: string,
  cloudProjectNumber: string,
): Promise<string> {
  if (!Capacitor.isPluginAvailable('CapsicoIntegrity')) {
    throw new Error('CapsicoIntegrity plugin not available');
  }
  const result = await CapsicoIntegrity.requestToken({ nonce, cloudProjectNumber });
  if (!result.token) throw new Error('Empty integrity token');
  return result.token;
}
